import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";

interface CustomSolutionModalProps {
  open: boolean;
  onClose: () => void;
}

const CustomSolutionModal: React.FC<CustomSolutionModalProps> = ({
  open,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    industry: "",
    projectType: "",
    budget: "",
    timeline: "",
    currentInfrastructure: "",
    expectedTraffic: "",
    complianceRequirements: [] as string[],
    additionalServices: [] as string[],
    projectDescription: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const name = e.target.name;
    const value = e.target.value;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCheckboxChange = (field: "complianceRequirements" | "additionalServices") => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.name;
    const currentArray = formData[field];
    
    if (event.target.checked) {
      setFormData({
        ...formData,
        [field]: [...currentArray, value],
      });
    } else {
      setFormData({
        ...formData,
        [field]: currentArray.filter((item) => item !== value),
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch("https://contact.isshosting.com/form-submission", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          formType: "custom_enterprise_solution",
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          industry: formData.industry,
          projectType: formData.projectType,
          budget: formData.budget,
          timeline: formData.timeline,
          currentInfrastructure: formData.currentInfrastructure,
          expectedTraffic: formData.expectedTraffic,
          complianceRequirements: formData.complianceRequirements,
          additionalServices: formData.additionalServices,
          projectDescription: formData.projectDescription,
        }),
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: "Your custom solution request has been sent! Our enterprise team will contact you within 24 hours.",
          severity: "success",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          industry: "",
          projectType: "",
          budget: "",
          timeline: "",
          currentInfrastructure: "",
          expectedTraffic: "",
          complianceRequirements: [],
          additionalServices: [],
          projectDescription: "",
        });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to send message. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            borderRadius: 3,
            border: "1px solid rgba(0, 168, 255, 0.3)",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            pb: 2,
            borderBottom: "1px solid rgba(0, 168, 255, 0.2)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 56,
              height: 56,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
              flexShrink: 0,
            }}
          >
            <BusinessCenterIcon sx={{ fontSize: 32, color: "white" }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Custom Enterprise Solution
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Tell us about your unique requirements
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "primary.main",
                bgcolor: "rgba(0, 168, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <form onSubmit={handleSubmit} id="custom-solution-form">
            <Grid container spacing={3}>
              {/* Contact Information */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}
                >
                  Contact Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": { borderColor: "primary.main" },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": { borderColor: "primary.main" },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number *"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": { borderColor: "primary.main" },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company/Organization *"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": { borderColor: "primary.main" },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Industry</InputLabel>
                  <Select
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    label="Industry"
                  >
                    <MenuItem value="Technology">Technology</MenuItem>
                    <MenuItem value="Healthcare">Healthcare</MenuItem>
                    <MenuItem value="Finance">Finance</MenuItem>
                    <MenuItem value="E-commerce">E-commerce</MenuItem>
                    <MenuItem value="Education">Education</MenuItem>
                    <MenuItem value="Government">Government</MenuItem>
                    <MenuItem value="Media & Entertainment">Media & Entertainment</MenuItem>
                    <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Project Details */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}
                >
                  Project Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Project Type</InputLabel>
                  <Select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    label="Project Type"
                  >
                    <MenuItem value="New Infrastructure">New Infrastructure</MenuItem>
                    <MenuItem value="Migration">Migration from Existing Provider</MenuItem>
                    <MenuItem value="Expansion">Infrastructure Expansion</MenuItem>
                    <MenuItem value="Disaster Recovery">Disaster Recovery Solution</MenuItem>
                    <MenuItem value="Hybrid Cloud">Hybrid Cloud Setup</MenuItem>
                    <MenuItem value="Multi-Region Deployment">Multi-Region Deployment</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Budget Range</InputLabel>
                  <Select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    label="Budget Range"
                  >
                    <MenuItem value="Under $5,000">Under $5,000</MenuItem>
                    <MenuItem value="$5,000 - $10,000">$5,000 - $10,000</MenuItem>
                    <MenuItem value="$10,000 - $25,000">$10,000 - $25,000</MenuItem>
                    <MenuItem value="$25,000 - $50,000">$25,000 - $50,000</MenuItem>
                    <MenuItem value="$50,000 - $100,000">$50,000 - $100,000</MenuItem>
                    <MenuItem value="$100,000+">$100,000+</MenuItem>
                    <MenuItem value="Flexible">Flexible</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Timeline</InputLabel>
                  <Select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    label="Timeline"
                  >
                    <MenuItem value="Immediate (1-2 weeks)">Immediate (1-2 weeks)</MenuItem>
                    <MenuItem value="1 Month">1 Month</MenuItem>
                    <MenuItem value="2-3 Months">2-3 Months</MenuItem>
                    <MenuItem value="3-6 Months">3-6 Months</MenuItem>
                    <MenuItem value="6+ Months">6+ Months</MenuItem>
                    <MenuItem value="Flexible">Flexible</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Technical Requirements */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}
                >
                  Technical Requirements
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Current Infrastructure</InputLabel>
                  <Select
                    name="currentInfrastructure"
                    value={formData.currentInfrastructure}
                    onChange={handleChange}
                    label="Current Infrastructure"
                  >
                    <MenuItem value="On-Premises">On-Premises</MenuItem>
                    <MenuItem value="Shared Hosting">Shared Hosting</MenuItem>
                    <MenuItem value="VPS">VPS</MenuItem>
                    <MenuItem value="Dedicated Servers">Dedicated Servers</MenuItem>
                    <MenuItem value="Cloud (AWS/Azure/GCP)">Cloud (AWS/Azure/GCP)</MenuItem>
                    <MenuItem value="Hybrid">Hybrid</MenuItem>
                    <MenuItem value="None - Starting Fresh">None - Starting Fresh</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Expected Traffic</InputLabel>
                  <Select
                    name="expectedTraffic"
                    value={formData.expectedTraffic}
                    onChange={handleChange}
                    label="Expected Traffic"
                  >
                    <MenuItem value="< 10K visitors/month">&lt; 10K visitors/month</MenuItem>
                    <MenuItem value="10K - 100K visitors/month">10K - 100K visitors/month</MenuItem>
                    <MenuItem value="100K - 500K visitors/month">100K - 500K visitors/month</MenuItem>
                    <MenuItem value="500K - 1M visitors/month">500K - 1M visitors/month</MenuItem>
                    <MenuItem value="1M+ visitors/month">1M+ visitors/month</MenuItem>
                    <MenuItem value="Variable/Seasonal">Variable/Seasonal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Compliance Requirements */}
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
                >
                  Compliance Requirements
                </Typography>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="HIPAA"
                        checked={formData.complianceRequirements.includes("HIPAA")}
                        onChange={handleCheckboxChange("complianceRequirements")}
                      />
                    }
                    label="HIPAA"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="PCI DSS"
                        checked={formData.complianceRequirements.includes("PCI DSS")}
                        onChange={handleCheckboxChange("complianceRequirements")}
                      />
                    }
                    label="PCI DSS"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="GDPR"
                        checked={formData.complianceRequirements.includes("GDPR")}
                        onChange={handleCheckboxChange("complianceRequirements")}
                      />
                    }
                    label="GDPR"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="SOC 2"
                        checked={formData.complianceRequirements.includes("SOC 2")}
                        onChange={handleCheckboxChange("complianceRequirements")}
                      />
                    }
                    label="SOC 2"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="FedRAMP"
                        checked={formData.complianceRequirements.includes("FedRAMP")}
                        onChange={handleCheckboxChange("complianceRequirements")}
                      />
                    }
                    label="FedRAMP"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="ISO 27001"
                        checked={formData.complianceRequirements.includes("ISO 27001")}
                        onChange={handleCheckboxChange("complianceRequirements")}
                      />
                    }
                    label="ISO 27001"
                  />
                </FormGroup>
              </Grid>

              {/* Additional Services */}
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
                >
                  Additional Services Needed
                </Typography>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="24/7 Monitoring"
                        checked={formData.additionalServices.includes("24/7 Monitoring")}
                        onChange={handleCheckboxChange("additionalServices")}
                      />
                    }
                    label="24/7 Monitoring"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="Managed Security"
                        checked={formData.additionalServices.includes("Managed Security")}
                        onChange={handleCheckboxChange("additionalServices")}
                      />
                    }
                    label="Managed Security"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="Database Management"
                        checked={formData.additionalServices.includes("Database Management")}
                        onChange={handleCheckboxChange("additionalServices")}
                      />
                    }
                    label="Database Management"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="Load Balancing"
                        checked={formData.additionalServices.includes("Load Balancing")}
                        onChange={handleCheckboxChange("additionalServices")}
                      />
                    }
                    label="Load Balancing"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="CDN Integration"
                        checked={formData.additionalServices.includes("CDN Integration")}
                        onChange={handleCheckboxChange("additionalServices")}
                      />
                    }
                    label="CDN Integration"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="Migration Assistance"
                        checked={formData.additionalServices.includes("Migration Assistance")}
                        onChange={handleCheckboxChange("additionalServices")}
                      />
                    }
                    label="Migration Assistance"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="Disaster Recovery"
                        checked={formData.additionalServices.includes("Disaster Recovery")}
                        onChange={handleCheckboxChange("additionalServices")}
                      />
                    }
                    label="Disaster Recovery"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="DevOps Support"
                        checked={formData.additionalServices.includes("DevOps Support")}
                        onChange={handleCheckboxChange("additionalServices")}
                      />
                    }
                    label="DevOps Support"
                  />
                </FormGroup>
              </Grid>

              {/* Project Description */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}
                >
                  Project Description
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Describe your project and specific requirements *"
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleChange}
                  multiline
                  rows={6}
                  required
                  placeholder="Please provide details about your project goals, technical requirements, scalability needs, security concerns, and any other relevant information..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": { borderColor: "primary.main" },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2, borderTop: "1px solid rgba(0, 168, 255, 0.2)" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={loading}
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": {
                borderColor: "primary.light",
                bgcolor: "rgba(0, 168, 255, 0.1)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="custom-solution-form"
            variant="contained"
            disabled={loading}
            sx={{
              background: "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5dcdff 0%, #3385db 100%)",
                boxShadow: "0 8px 24px rgba(0, 168, 255, 0.4)",
              },
              "&:disabled": {
                background: "rgba(0, 168, 255, 0.5)",
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} sx={{ color: "white" }} />
                Submitting...
              </Box>
            ) : (
              "Submit Request"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CustomSolutionModal;

